'use client';

/**
 * LegendItem Component
 *
 * A color-coded legend item for workflow visualizations.
 * Features a colored left border/icon with a label.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef } from 'react';
import { type AccentColor, accentColors } from '../../theme/palette';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export type LegendVariant = 'pill' | 'card' | 'inline';

export interface LegendItemProps extends Omit<BoxProps, 'color'> {
  /**
   * Legend label
   */
  label: string;

  /**
   * Accent color
   * @default 'teal'
   */
  color?: AccentColor;

  /**
   * Optional icon to display
   */
  icon?: React.ReactNode;

  /**
   * Display variant
   * @default 'card'
   */
  variant?: LegendVariant;

  /**
   * Whether this item is active/selected
   * @default false
   */
  active?: boolean;

  /**
   * Size variant
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCardProps {
  $color: string;
  $isDark: boolean;
  $active: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledCard = styled(
  Box,
  transientOptions,
)<StyledCardProps>(({ theme, $color, $isDark, $active, $size }) => {
  const paddingMap = {
    small: theme.spacing(0.75, 1.5),
    medium: theme.spacing(1.25, 2),
    large: theme.spacing(1.5, 2.5),
  };

  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: paddingMap[$size],
    borderRadius: 8,
    borderLeft: `3px solid ${$color}`,
    background: $isDark ? alpha($color, $active ? 0.15 : 0.08) : alpha($color, $active ? 0.12 : 0.06),
    transition: 'all 0.2s ease-in-out',
    cursor: 'default',

    '&:hover': {
      background: $isDark ? alpha($color, 0.15) : alpha($color, 0.1),
    },
  };
});

interface StyledPillProps {
  $color: string;
  $isDark: boolean;
  $active: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledPill = styled(
  Box,
  transientOptions,
)<StyledPillProps>(({ theme, $color, $isDark, $active, $size }) => {
  const paddingMap = {
    small: theme.spacing(0.5, 1),
    medium: theme.spacing(0.75, 1.5),
    large: theme.spacing(1, 2),
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: paddingMap[$size],
    borderRadius: 20,
    background: $isDark ? alpha($color, $active ? 0.2 : 0.1) : alpha($color, $active ? 0.15 : 0.08),
    border: `1px solid ${alpha($color, $isDark ? 0.3 : 0.2)}`,
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
      background: $isDark ? alpha($color, 0.2) : alpha($color, 0.12),
    },
  };
});

interface StyledInlineProps {
  $size: 'small' | 'medium' | 'large';
}

const StyledInline = styled(
  Box,
  transientOptions,
)<StyledInlineProps>(({ theme, $size }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing($size === 'small' ? 0.5 : 0.75),
}));

const ColorDot = styled(
  'span',
  transientOptions,
)<{
  $color: string;
  $size: 'small' | 'medium' | 'large';
}>(({ $color, $size }) => {
  const sizeMap = { small: 6, medium: 8, large: 10 };
  return {
    width: sizeMap[$size],
    height: sizeMap[$size],
    borderRadius: '50%',
    backgroundColor: $color,
    flexShrink: 0,
  };
});

// ============================================================================
// Component
// ============================================================================

export const LegendItem = forwardRef<HTMLDivElement, LegendItemProps>(
  ({ label, color = 'teal', icon, variant = 'card', active = false, size = 'medium', ...props }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const accentColor = accentColors[color];

    const fontSizeMap = {
      small: '0.75rem',
      medium: '0.8125rem',
      large: '0.875rem',
    };

    const content = (
      <>
        {icon && (
          <Box
            sx={{
              color: accentColor,
              display: 'flex',
              alignItems: 'center',
              fontSize: size === 'small' ? 14 : size === 'medium' ? 16 : 18,
              '& svg': {
                width: size === 'small' ? 14 : size === 'medium' ? 16 : 18,
                height: size === 'small' ? 14 : size === 'medium' ? 16 : 18,
              },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          sx={{
            fontSize: fontSizeMap[size],
            fontWeight: 500,
            color: isDark ? accentColor : theme.palette.text.primary,
          }}
        >
          {label}
        </Typography>
      </>
    );

    if (variant === 'pill') {
      return (
        <StyledPill ref={ref} $color={accentColor} $isDark={isDark} $active={active} $size={size} {...props}>
          {content}
        </StyledPill>
      );
    }

    if (variant === 'inline') {
      return (
        <StyledInline ref={ref} $size={size} {...props}>
          <ColorDot $color={accentColor} $size={size} />
          <Typography
            sx={{
              fontSize: fontSizeMap[size],
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
        </StyledInline>
      );
    }

    // Default: card variant
    return (
      <StyledCard ref={ref} $color={accentColor} $isDark={isDark} $active={active} $size={size} {...props}>
        {content}
      </StyledCard>
    );
  },
);

LegendItem.displayName = 'LegendItem';

export default LegendItem;
